/**
 * LES TÂCHES EN COLONNES — module PUR (RQ, 05/10/2026).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * « Si les tâches pouvaient être affichées en colonnes ce serait beaucoup
 * mieux. »
 *
 * Une check-list s'affichait sur une seule colonne. Celle du prélèvement en
 * compte 73, et le mémo de garde en aligne 126 (la garde appelle la réception
 * et le prélèvement) : il faut faire défiler plusieurs écrans pour voir ce
 * qu'on doit faire, et l'on perd la vue d'ensemble que la grille papier donne
 * d'un coup d'œil.
 *
 * ── ⚠️ TROIS PIÈGES DES COLONNES CSS, et comment ils sont traités ─────────
 *
 * ① UNE CASE À COCHER COUPÉE DE SON TEXTE. Le multi-colonnes découpe où il
 *    veut : sans `breakInside: 'avoid'`, la case se retrouve en bas d'une
 *    colonne et son libellé en haut de la suivante. On coche alors la mauvaise
 *    tâche, et rien ne le signale. C'est le piège le plus grave ici, parce
 *    qu'il produit une erreur SILENCIEUSE sur une liste de contrôle.
 *
 * ② DEUX COLONNES ILLISIBLES SUR UN ÉCRAN ÉTROIT. On déclare donc une LARGEUR
 *    MINIMALE (`columnWidth`) en plus du nombre : le navigateur retombe de
 *    lui-même à une colonne quand la place manque. Un `columnCount` seul
 *    aurait coupé le texte en lanières de quinze caractères sur un portable.
 *
 * ③ DEUX COLONNES POUR TROIS TÂCHES. Le découpage n'aide qu'à partir d'une
 *    certaine longueur ; en dessous il déséquilibre la rubrique sans rien
 *    gagner. D'où le seuil ci-dessous.
 *
 * ── Ce qu'on ne change PAS : l'ordre de lecture ───────────────────────────
 *
 * Les colonnes CSS remplissent la première de haut en bas, puis la deuxième —
 * l'ordre du journal. C'est bien l'ordre des tâches qui est conservé, et c'est
 * important : les listes de garde sont NUMÉROTÉES dans le document source
 * (1 → 31), donc la suite se lit. Un remplissage en lignes (gauche à droite)
 * aurait mélangé la séquence.
 *
 * ⚠️ MODULE SANS AUCUN IMPORT (feuille) : compilé et éprouvé par `npm test`
 * (préfixe `front/`). Il ne rend que des valeurs, jamais de JSX — c'est ce qui
 * permet de l'éprouver hors navigateur.
 */

/**
 * En dessous de ce nombre de tâches, une rubrique reste sur UNE colonne.
 *
 * Six : à cinq tâches, deux colonnes de deux ou trois lignes se lisent plus mal
 * qu'une liste courte, et la rubrique paraît tronquée. Le chiffre est un choix
 * d'affichage, pas une mesure — mais il est à UN endroit, et le test le fixe
 * pour qu'il ne se mette pas à varier d'un écran à l'autre.
 */
export const SEUIL_COLONNES = 6;

/** La largeur minimale d'une colonne de tâches, en pixels. En dessous, le
 *  navigateur en met moins — voir le piège ② de l'en-tête. */
export const LARGEUR_MIN_COLONNE = 250;

/** Le nombre de colonnes VOULU pour n tâches. Jamais plus de trois : au-delà,
 *  les colonnes deviennent trop étroites pour une phrase, même sur un grand
 *  écran. */
export function nombreDeColonnes(nbTaches: number, max = 2): number {
  const n = Number.isFinite(nbTaches) ? Math.max(0, Math.floor(nbTaches)) : 0;
  if (n < SEUIL_COLONNES) return 1;
  const plafond = Math.min(3, Math.max(1, Math.floor(max)));
  /* On ne demande pas trois colonnes pour huit tâches : on veut au moins
     `SEUIL_COLONNES` par colonne, sinon on retombe à moins de colonnes. */
  return Math.max(1, Math.min(plafond, Math.floor(n / SEUIL_COLONNES)));
}

export interface StyleColonnes {
  columnCount?: number;
  columnWidth?: string;
  columnGap?: string;
  /** Un trait entre les colonnes : sans lui, deux colonnes de texte à la même
   *  hauteur se lisent comme une seule phrase qui traverse. */
  columnRule?: string;
}

/**
 * Le style du CONTENEUR de tâches. Rend un objet VIDE sous le seuil : on ne
 * pose aucune propriété de colonne plutôt que `columnCount: 1`, pour ne pas
 * créer un contexte de colonnes là où il n'en faut pas (il modifie le calcul
 * des marges et des flottants).
 */
export function styleColonnes(nbTaches: number, max = 2): StyleColonnes {
  const n = nombreDeColonnes(nbTaches, max);
  if (n <= 1) return {};
  return {
    columnCount: n,
    columnWidth: `${LARGEUR_MIN_COLONNE}px`,
    columnGap: '22px',
    columnRule: '1px solid var(--border-subtle, #F0F2F5)',
  };
}

/**
 * Le style de CHAQUE TÂCHE dans un conteneur en colonnes.
 *
 * ⚠️ `breakInside: 'avoid'` EST LE CŒUR DE CE MODULE, pas un raffinement : il
 * empêche le piège ① (case à cocher séparée de son libellé par une coupure de
 * colonne, donc coche sur la mauvaise ligne). Toute tâche rendue dans un
 * conteneur en colonnes doit le porter.
 */
export const STYLE_TACHE_COLONNE = {
  breakInside: 'avoid',
  WebkitColumnBreakInside: 'avoid',
  pageBreakInside: 'avoid',
} as const;
